import { classNames, isValidHour, isValidMinute } from '@library/utils';
import type { DateObjectUnits } from 'luxon';
import { NumberInput } from '@library/components/inputs';
import { Select } from '@library/components/selects';
import type { SelectOptions } from '@library/types/utils';
import { TimeErrorMessages } from '@library/components/messages/TimeBoundaryErrorMessages';
import { TimePickerContext } from '@library/components/datePicker/TimePickerContext';
import { blockInvalidNumberCharIncludingNegative } from '@library/component-utils/input';
import colonIcon from '@library/assets/images/icons/colon-icon.svg';
import { convertToMilitaryHour } from '@library/component-utils/dateTime';
import { useContext } from 'react';

export const TimePicker = () => {
  const {
    date: newDate,
    displayValues,
    maxDate,
    minDate,
    placeholderSplitTime: contextPlaceholderSplitTime,
    onChange = () => {},
    setDisplayValues,
    setProposedTime,
    validateTime,
    splitTime: contextSplitTime,
    validations
  } = useContext(TimePickerContext);

  const { isHourError, isMinuteError } = validations;
  const { hour: displayHour, minutes: displayMinutes } = displayValues;

  const { hour, minutes, amPm } = contextSplitTime;

  const {
    amPm: initialAmPm,
    hour: placeholderHour,
    minutes: placeholderMinutes
  } = contextPlaceholderSplitTime;

  const selectOptions: SelectOptions<'AM' | 'PM'> = [
    { key: 'AM', name: 'AM', value: 'AM' },
    { key: 'PM', name: 'PM', value: 'PM' }
  ];

  const handleOnBlur = (inputName: 'hour' | 'minutes') => {
    const displayValue = displayValues[inputName];
    const placeholderValue = contextPlaceholderSplitTime[inputName];
    const value = contextSplitTime[inputName];

    const validationFunction =
      inputName === 'hour' ? isValidHour : isValidMinute;

    if (
      displayValue === '' ||
      Number(displayValue) === Number(placeholderValue)
    ) {
      setDisplayValues({ ...displayValues, [inputName]: '' });
    } else if (validationFunction(displayValue)) {
      setDisplayValues({ ...displayValues, [inputName]: value });
    }
  };

  const handleOnValueChange = ({
    inputName,
    value
  }: {
    inputName: 'hour' | 'minutes';
    value: string;
  }) => {
    if (value.length > 2) {
      return;
    }

    const newTime =
      inputName === 'hour'
        ? newDate.set({ hour: convertToMilitaryHour({ amPm, hour: value }) })
        : newDate.set({ minute: Number(value) });

    const isValid =
      inputName === 'hour' ? isValidHour(value) : isValidMinute(value);

    if (isValid) {
      setProposedTime(newTime);

      const onChangeValue: DateObjectUnits =
        inputName === 'hour'
          ? {
              hour: convertToMilitaryHour({ amPm, hour: value }),
              minute: Number(minutes)
            }
          : {
              hour: convertToMilitaryHour({ amPm, hour }),
              minute: Number(value)
            };

      onChange(onChangeValue);
    }

    const newSplit = {
      ...displayValues,
      [inputName]: value
    };

    setDisplayValues(newSplit);

    validateTime({
      inputName,
      newTime,
      value
    });
  };

  return (
    <div className="flex flex-col pb-2" data-testid="time-picker">
      <div className="font-heebo text-soClean-130 flex h-16 w-72 items-center justify-center gap-2.5 rounded">
        <div className="w-9 text-sm font-medium">TIME</div>
        <div className="bg-soFresh-110 flex items-center justify-center gap-0.5 font-normal">
          <NumberInput
            className={classNames(
              'focus:border-msJackson placeholder:text-soClean-150 focus:bg-soFresh-120 hover:bg-soFresh-120 bg-soFresh-110 h-10 !w-14 shrink grow basis-0 justify-center rounded-md border py-3 pl-5 text-sm focus:placeholder:opacity-50',
              isHourError
                ? 'border-roses focus:border-roses'
                : 'focus:border-msJackson',
              !isHourError && displayHour.length > 0 ? 'border-msJackson' : '',
              !isHourError && displayHour.length === 0
                ? 'border-soFresh-140'
                : ''
            )}
            data-testid="hours"
            id="hours"
            onBlur={() => {
              handleOnBlur('hour');
            }}
            onKeyDown={blockInvalidNumberCharIncludingNegative}
            onValueChange={(value: string) => {
              handleOnValueChange({ inputName: 'hour', value });
            }}
            placeholder={placeholderHour}
            value={displayHour}
          />
          <img className="px-2" src={colonIcon} alt="colon icon" />
          <NumberInput
            className={classNames(
              'focus:border-msJackson focus:bg-soFresh-120 hover:bg-soFresh-120 bg-soFresh-110 placeholder:text-soClean-150 h-10 !w-14 shrink grow basis-0 justify-center rounded-md border py-3 pl-5 text-sm focus:placeholder:opacity-50',
              isMinuteError
                ? 'border-roses focus:border-roses'
                : 'focus:border-msJackson',
              !isMinuteError && displayMinutes.length > 0
                ? 'border-msJackson'
                : '',
              !isMinuteError && displayMinutes.length === 0
                ? 'border-soFresh-140'
                : ''
            )}
            data-testid="minutes"
            id="minutes"
            onBlur={() => {
              handleOnBlur('minutes');
            }}
            onKeyDown={blockInvalidNumberCharIncludingNegative}
            onValueChange={(value: string) => {
              handleOnValueChange({ inputName: 'minutes', value });
            }}
            placeholder={placeholderMinutes}
            value={displayMinutes}
          />
          <img className="px-2" src={colonIcon} alt="colon icon" />
          <Select
            allOption={false}
            className={classNames(
              initialAmPm !== amPm ? '!border-msJackson' : ''
            )}
            onChange={(option) => {
              const militaryHour = convertToMilitaryHour({
                amPm: option.value,
                hour
              });

              const newTime = newDate.set({ hour: militaryHour });

              setProposedTime(newTime);
              onChange({ hour: militaryHour, minute: Number(minutes) });

              validateTime({
                inputName: 'hour',
                newTime,
                value: hour
              });
            }}
            label=""
            options={selectOptions}
            size="small"
            testId="am-pm"
            value={amPm}
          />
        </div>
      </div>

      <TimeErrorMessages
        maxTime={maxDate}
        minTime={minDate}
        validations={validations}
      />
    </div>
  );
};
